/**
 * 甘特图状态管理 Hook
 *
 * 管理缩放级别、日期范围、筛选条件等状态，支持 localStorage 持久化
 */

import { startOfDay, startOfWeek, startOfMonth, startOfQuarter, startOfYear, endOfWeek, endOfMonth, endOfQuarter, endOfYear } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import * as React from 'react'

import {
  getDateRangeForZoom,
  navigateDateRange,
  calculateAutoFitDateRange,
  hasTasksOutsideView as checkTasksOutsideView,
} from '@/components/pm/gantt/utils'

import type {
  ZoomLevel,
  DateRange,
  GanttFilters,
  GroupByOption,
  UseGanttStateReturn,
  DateRangePreset,
  GanttTask,
} from '@/components/pm/gantt/types'


const STORAGE_KEY_PREFIX = 'gantt-state'

interface StoredState {
  zoomLevel: ZoomLevel
  groupBy: GroupByOption
  showDependencies: boolean
  dateRangePreset: DateRangePreset
}

/**
 * 从 localStorage 读取状态
 */
function loadStoredState(projectId: string): Partial<StoredState> {
  try {
    const key = `${STORAGE_KEY_PREFIX}-${projectId}`
    const stored = localStorage.getItem(key)
    if (stored) {
      return JSON.parse(stored) as Partial<StoredState>
    }
  } catch {
    // 忽略解析错误
  }
  return {}
}

/**
 * 保存状态到 localStorage
 */
function saveStoredState(projectId: string, state: StoredState) {
  try {
    const key = `${STORAGE_KEY_PREFIX}-${projectId}`
    localStorage.setItem(key, JSON.stringify(state))
  } catch {
    // 忽略存储错误
  }
}

/**
 * 根据预设获取日期范围
 */
function getDateRangeForPreset(preset: Exclude<DateRangePreset, 'auto' | 'custom'>): DateRange {
  const today = startOfDay(new Date())

  switch (preset) {
    case 'week':
      return {
        start: startOfWeek(today, { locale: zhCN }),
        end: endOfWeek(today, { locale: zhCN }),
      }
    case 'month':
      return {
        start: startOfMonth(today),
        end: endOfMonth(today),
      }
    case 'quarter':
      return {
        start: startOfQuarter(today),
        end: endOfQuarter(today),
      }
    case 'year':
      return {
        start: startOfYear(today),
        end: endOfYear(today),
      }
  }
}

interface UseGanttStateOptions {
  /** 项目 ID */
  projectId: string
  /** 初始缩放级别 */
  initialZoomLevel?: ZoomLevel
}

export function useGanttState({
  projectId,
  initialZoomLevel = 'week',
}: UseGanttStateOptions): UseGanttStateReturn {
  // 从 localStorage 加载初始状态
  const storedState = React.useMemo(
    () => loadStoredState(projectId),
    [projectId]
  )

  // 缩放级别
  const [zoomLevel, setZoomLevelInternal] = React.useState<ZoomLevel>(
    storedState.zoomLevel || initialZoomLevel
  )

  // 日期范围预设
  const [dateRangePreset, setDateRangePresetInternal] = React.useState<DateRangePreset>(
    storedState.dateRangePreset || 'month'
  )

  // 日期范围
  const [dateRange, setDateRange] = React.useState<DateRange>(() => {
    const preset = storedState.dateRangePreset || 'month'
    if (preset === 'auto' || preset === 'custom') {
      return getDateRangeForZoom(new Date(), zoomLevel)
    }
    return getDateRangeForPreset(preset)
  })

  // 筛选条件
  const [filters, setFilters] = React.useState<GanttFilters>({})

  // 分组方式
  const [groupBy, setGroupByInternal] = React.useState<GroupByOption>(
    storedState.groupBy || 'none'
  )

  // 是否显示依赖线
  const [showDependencies, setShowDependencies] = React.useState(
    storedState.showDependencies ?? true
  )

  // 展开的分组
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    new Set()
  )

  // 设置缩放级别（同时更新日期范围）
  const setZoomLevel = React.useCallback(
    (level: ZoomLevel) => {
      setZoomLevelInternal(level)
      // 以当前日期范围的中心点为基准重新计算
      const centerDate = new Date(
        (dateRange.start.getTime() + dateRange.end.getTime()) / 2
      )
      setDateRange(getDateRangeForZoom(centerDate, level))
      setDateRangePresetInternal('custom')
    },
    [dateRange]
  )

  // 设置日期范围预设
  const setDateRangePreset = React.useCallback((preset: DateRangePreset) => {
    setDateRangePresetInternal(preset)
    if (preset !== 'auto' && preset !== 'custom') {
      setDateRange(getDateRangeForPreset(preset))
    }
  }, [])

  // 自动适配到任务范围
  const autoFit = React.useCallback((tasks: GanttTask[]) => {
    const range = calculateAutoFitDateRange(tasks)
    setDateRange(range)
    setDateRangePresetInternal('auto')
  }, [])

  // 检查是否有任务在视图外
  const hasTasksOutsideView = React.useCallback(
    (tasks: GanttTask[]) => {
      return checkTasksOutsideView(tasks, dateRange)
    },
    [dateRange]
  )

  // 设置分组方式
  const setGroupBy = React.useCallback((newGroupBy: GroupByOption) => {
    setGroupByInternal(newGroupBy)
    // 切换分组时，默认展开所有分组
    setExpandedGroups(new Set())
  }, [])

  // 切换依赖线显示
  const toggleDependencies = React.useCallback(() => {
    setShowDependencies((prev) => !prev)
  }, [])

  // 切换分组展开状态
  const toggleGroup = React.useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  // 滚动到指定日期
  const scrollToDate = React.useCallback(
    (date: Date) => {
      setDateRange(getDateRangeForZoom(date, zoomLevel))
      setDateRangePresetInternal('custom')
    },
    [zoomLevel]
  )

  // 滚动到今天
  const scrollToToday = React.useCallback(() => {
    scrollToDate(new Date())
  }, [scrollToDate])

  // 导航（前进/后退）
  const navigate = React.useCallback(
    (direction: 'prev' | 'next') => {
      setDateRange((prev) => navigateDateRange(prev, direction, zoomLevel))
      setDateRangePresetInternal('custom')
    },
    [zoomLevel]
  )

  // 持久化状态到 localStorage
  React.useEffect(() => {
    saveStoredState(projectId, {
      zoomLevel,
      groupBy,
      showDependencies,
      dateRangePreset,
    })
  }, [projectId, zoomLevel, groupBy, showDependencies, dateRangePreset])

  return {
    zoomLevel,
    setZoomLevel,
    dateRange,
    setDateRange,
    scrollToDate,
    scrollToToday,
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    showDependencies,
    toggleDependencies,
    expandedGroups,
    toggleGroup,
    navigate,
    dateRangePreset,
    setDateRangePreset,
    autoFit,
    hasTasksOutsideView,
  }
}
